import {
	Box,
	Button,
	Card,
	CardActions,
	Grid,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useGetAllEnumListQuery, useGetDicListQuery } from '@/api/common';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
	useEditWorkMutation,
	useGetAdmissionRequestDetailQuery,
} from '@/api/member-management/admissionRequest';
import { EditWorkRequest } from '@/api/DTO/member-management/admissionRequest';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import { YESNO, YESNOLIST } from '@/common/constants/common';

interface Props {
	type: string;
}
export default function EditWorkInformation(props: Props) {
	const { type } = props;
	const memberType = i18n.t(
		type === 'admissionRequest'
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { admissionRequestId } = useParams();
	const [editWork] = useEditWorkMutation();

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: details } = useGetAdmissionRequestDetailQuery(
		admissionRequestId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const { data: financialConditionDicList } = useGetDicListQuery(
		'financial_condition',
		{ refetchOnMountOrArgChange: true }
	);
	const { data: workStatusDicList } = useGetDicListQuery('work_status', {
		refetchOnMountOrArgChange: true,
	});
	const { data: educationDicList } = useGetDicListQuery('education', {
		refetchOnMountOrArgChange: true,
	});
	const { data: livingStatusDicList } = useGetDicListQuery('living_status', {
		refetchOnMountOrArgChange: true,
	});
	const { data: familySupportDegreeDicList } = useGetDicListQuery(
		'degree_of_family_support',
		{ refetchOnMountOrArgChange: true }
	);

	const defaultValues: DefaultValues<EditWorkRequest> = {};

	const formSchema = Yup.object().shape({
		chiFullNameOfSecondarySchool: Yup.string().when('studyingSecondarySchool', {
			is: (studyingSecondarySchool: string) => studyingSecondarySchool,
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		nameOfOtherYouthService: Yup.string().when('attendingOtherYouthService', {
			is: (attendingOtherYouthService: string) => attendingOtherYouthService,
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
		nameOfResidentialCareHome: Yup.string().when('livingStatus', {
			is: (livingStatus: string) => livingStatus && livingStatusIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
		mode: 'onChange',
		shouldFocusError: true,
	});

	const livingStatusIsOther = () => {
		const obj = livingStatusDicList?.data.find(
			(item: any) => item.id == watchLivingStatus
		);
		if (obj && obj.code == 5) return true;
		return false;
	};
	const watchLivingStatus = methods.watch('livingStatus');
	const watchStudyingSecondarySchool = methods.watch('studyingSecondarySchool');
	const watchAttendingOtherYouthService = methods.watch(
		'attendingOtherYouthService'
	);

	useEffect(() => {
		if (details) {
			methods.reset(details.data);
			methods.setValue(
				'attendingOtherYouthService',
				details.data.attendingOtherYouthService == null
					? null
					: !!details.data.attendingOtherYouthService
			);
			methods.setValue(
				'studyingSecondarySchool',
				details.data.studyingSecondarySchool == null
					? null
					: !!details.data.studyingSecondarySchool
			);
		}
	}, [details]);

	const onSubmit: SubmitHandler<EditWorkRequest> = async (data) => {
		// The optional Yes/No dropdowns emit '' for "None"; send null so it is not stored as No.
		const yesNoOrNull = (value: unknown) =>
			value === '' || value == null ? null : !!value;
		const obj: EditWorkRequest = {
			...data,
			studyingSecondarySchool: yesNoOrNull(data.studyingSecondarySchool),
			attendingOtherYouthService: yesNoOrNull(data.attendingOtherYouthService),
		};
		const response = await editWork(obj);

		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateMemberSuccess', { type: memberType }), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(
				`/member-management/${
					type == 'member' ? 'member-list' : 'admission-request-list'
				}/detail/${admissionRequestId}`
			);
		}
	};

	const handleClose = () => {
		navigate(
			`/member-management/${
				type == 'member' ? 'member-list' : 'admission-request-list'
			}/detail/${admissionRequestId}`,
			{ replace: true }
		);
	};
	return (
		<>
			<Box sx={{ width: '100%' }}>
				<FormProvider {...methods}>
					<form onSubmit={methods.handleSubmit(onSubmit)}>
						<Box display='flex' justifyContent='space-between'>
							<Typography variant='h4'>
								{type == 'admissionRequest'
									? t('drawerListItems.editAdmissionRequest')
									: t('drawerListItems.editMember')}
							</Typography>
						</Box>
						<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
							<Box
								display='flex'
								justifyContent='space-between'
								marginBottom={2}
							>
								<Typography variant='body1' sx={{ fontWeight: 'bold' }}>
									{t('member.educationWorkLive')}
								</Typography>
							</Box>
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid size={6}>
									<AppMultipleDropDownMenu
										control={methods.control}
										name='financialConditionList'
										label={t('preAdmission.financialCondition')}
										options={(financialConditionDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='workStatus'
										label={t('preAdmission.workStatus')}
										options={(workStatusDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='workMotivation'
										label={t('preAdmission.workMotivation')}
										options={(allEnumList?.data.WorkMotivationEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='education'
										label={t('preAdmission.education')}
										options={(educationDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='studyingSecondarySchool'
										label={t('preAdmission.studyingSecondarySchool')}
										options={YESNO}
										handleChange={() => {
											methods.setValue('chiFullNameOfSecondarySchool', '');
											methods.clearErrors('chiFullNameOfSecondarySchool');
										}}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										disabled={!watchStudyingSecondarySchool}
										control={methods.control}
										name='chiFullNameOfSecondarySchool'
										label={`${t(
											'preAdmission.studyingSecondarySchoolSpecify'
										)}${watchStudyingSecondarySchool ? '*' : ''}`}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='attendingOtherYouthService'
										label={t('preAdmission.attendingOtherYouthService')}
										options={YESNO}
										handleChange={() => {
											methods.setValue('nameOfOtherYouthService', '');
											methods.clearErrors('nameOfOtherYouthService');
										}}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										disabled={!watchAttendingOtherYouthService}
										control={methods.control}
										name='nameOfOtherYouthService'
										label={`${t(
											'preAdmission.attendingOtherYouthServiceSpecify'
										)}${watchAttendingOtherYouthService ? '*' : ''}`}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='livingStatus'
										label={t('preAdmission.livingStatus')}
										options={(livingStatusDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
										handleChange={() => {
											methods.setValue('nameOfResidentialCareHome', '');
											methods.clearErrors('nameOfResidentialCareHome');
										}}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										disabled={!livingStatusIsOther()}
										control={methods.control}
										name='nameOfResidentialCareHome'
										label={`${t('preAdmission.livingStatusSpecify')}${
											livingStatusIsOther() ? '*' : ''
										}`}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='livingWithPersonsAgedUnder18'
										label={t('preAdmission.livingWithPersonAgedUnder18')}
										options={(
											allEnumList?.data.LivingWithPersonsAgedUnder18Enum || []
										).map((item: any) => {
											return {
												...item,
												id: item.code,
												name: item.desc,
											};
										})}
									/>
								</Grid>
								<Grid size={6}>
									<AppTextField
										control={methods.control}
										name='noOfPeopleLivingTogether'
										label={t('preAdmission.excludePrincipalClient')}
									/>
								</Grid>
								<Grid size={6}>
									<AppDropDownMenu
										optional={true}
										control={methods.control}
										name='degreeOfFamilySupport'
										label={t('preAdmission.degreeFamilySupport')}
										options={(familySupportDegreeDicList?.data || []).map(
											(item: { labelEn: any }) => {
												return {
													...item,
													name: item.labelEn,
												};
											}
										)}
									/>
								</Grid>
							</Grid>
							<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
								<Button onClick={handleClose}>{t('button.cancel')}</Button>
								<Button
									type='submit'
									variant='contained'
									endIcon={<CheckCircleOutlineIcon />}
								>
									{t('button.complete')}
								</Button>
							</CardActions>
						</Card>
					</form>
				</FormProvider>
			</Box>
		</>
	);
}
